import React, { useState } from 'react';
import { Card, Button, Space, Typography, Row, Col, Divider, Alert } from 'antd';
import { RobotOutlined, SearchOutlined, MessageOutlined, BarChartOutlined, ShopOutlined } from '@ant-design/icons';
import AdvancedSearch from '../Search/AdvancedSearch';
import AISupportChat from '../UI/AISupportChat';
import AIVendorAnalysis from '../UI/AIVendorAnalysis';
import AIOrderInsights from '../UI/AIOrderInsights';
import AIInfoButton from '../UI/AIInfoButton';
import { useAISupport } from '../../contexts/AISupportContext';

const { Title, Text, Paragraph } = Typography;

const AITestPage = () => {
    const { openAISupport } = useAISupport();
    const [vendorAnalysisVisible, setVendorAnalysisVisible] = useState(false);
    const [orderInsightsVisible, setOrderInsightsVisible] = useState(false);

    // Mock data for testing
    const mockMenuItem = {
        id: 1,
        name: "Butter Chicken",
        description: "Creamy tomato-based curry with tender chicken pieces",
        price: 250,
        category: { name: "North Indian" },
        ingredients: ["Chicken", "Tomatoes", "Cream", "Butter", "Spices"],
        is_vegetarian: false,
        is_vegan: false,
        is_gluten_free: true,
        is_spicy: true,
        spice_level: 3,
        preparation_time: 45,
        portions_per_person: 0.2
    };

    const mockVendor = {
        id: 1,
        business_name: "Spice Garden Restaurant",
        description: "Authentic North Indian cuisine with modern twists",
        cuisines: ["North Indian", "Mughlai", "Tandoor"],
        rating: 4.5,
        total_reviews: 150,
        city: "Mumbai",
        years_in_business: 5
    };

    const mockOrders = [
        {
            id: 1,
            items: [{ name: "Butter Chicken", price: 250 }],
            total: 250,
            status: "delivered",
            created_at: "2024-01-15T19:30:00Z",
            delivery_info: { estimated_time: 45, actual_time: 42 }
        },
        {
            id: 2,
            items: [{ name: "Biryani", price: 300 }],
            total: 300,
            status: "delivered",
            created_at: "2024-01-10T20:15:00Z",
            delivery_info: { estimated_time: 50, actual_time: 48 }
        },
        {
            id: 3,
            items: [{ name: "Paneer Tikka", price: 200 }],
            total: 200,
            status: "delivered",
            created_at: "2024-01-05T18:45:00Z",
            delivery_info: { estimated_time: 40, actual_time: 38 }
        }
    ];

    const handleSearch = (query) => {
        console.log('Search:', query);
        alert(`Search performed: "${query}"`);
    };

    const handleEscalate = (escalationData) => {
        console.log('Escalation requested:', escalationData);
        alert('Escalating to human support agent...');
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="text-center mb-8">
                <Title level={1} className="flex items-center justify-center gap-3">
                    <RobotOutlined className="text-blue-500" />
                    AI Integration Test Page
                </Title>
                <Paragraph className="text-lg text-gray-600">
                    Test all AI-powered features integrated into the application
                </Paragraph>
            </div>

            <Alert
                message="AI Integration Status"
                description="AI services are integrated for menu analysis, support chat, vendor analysis, and order insights. Standard search is used for better performance and reliability."
                type="success"
                showIcon
                className="mb-6"
            />

            <Row gutter={[24, 24]}>
                {/* Standard Search */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <SearchOutlined className="text-blue-500" />
                                Standard Search
                            </div>
                        }
                        className="h-full"
                    >
                        <Paragraph>
                            Test the standard search functionality with suggestions and filters.
                        </Paragraph>
                        <AdvancedSearch
                            onSearch={handleSearch}
                            placeholder="Try searching for 'spicy vegetarian food'..."
                            compact={false}
                            showFilters={true}
                            showSuggestions={true}
                            showTrending={true}
                        />
                        <Divider />
                        <Text type="secondary">
                            Features: Search suggestions, filters, trending searches
                        </Text>
                    </Card>
                </Col>

                {/* AI Support Chat */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <MessageOutlined className="text-green-500" />
                                AI Customer Support
                            </div>
                        }
                        className="h-full"
                    >
                        <Paragraph>
                            Test the AI-powered customer support chat with intelligent query handling.
                        </Paragraph>
                        <Space direction="vertical" className="w-full">
                            <Button
                                type="primary"
                                icon={<MessageOutlined />}
                                onClick={openAISupport}
                                block
                            >
                                Open AI Support Chat
                            </Button>
                            <Text type="secondary">
                                Features: Query analysis, automatic escalation, conversation history
                            </Text>
                        </Space>
                    </Card>
                </Col>

                {/* AI Menu Item Analysis */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <RobotOutlined className="text-purple-500" />
                                AI Menu Item Analysis
                            </div>
                        }
                        className="h-full"
                    >
                        <Paragraph>
                            Test AI analysis of menu items with detailed information and insights.
                        </Paragraph>
                        <Space direction="vertical" className="w-full">
                            <div className="p-3 bg-gray-50 rounded">
                                <Text strong>{mockMenuItem.name}</Text>
                                <br />
                                <Text type="secondary">{mockMenuItem.description}</Text>
                            </div>
                            <AIInfoButton
                                menuItem={mockMenuItem}
                                type="primary"
                                size="large"
                                variant="gradient"
                                block
                                className="px-6 py-2 text-base font-semibold rounded-lg"
                            >
                                Get AI Analysis
                            </AIInfoButton>
                            <Text type="secondary">
                                Features: Overview, nutrition, cooking tips, ingredients, dietary info
                            </Text>
                        </Space>
                    </Card>
                </Col>

                {/* AI Vendor Analysis */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <ShopOutlined className="text-orange-500" />
                                AI Vendor Analysis
                            </div>
                        }
                        className="h-full"
                    >
                        <Paragraph>
                            Test AI analysis of vendor profiles with business insights and recommendations.
                        </Paragraph>
                        <Space direction="vertical" className="w-full">
                            <div className="p-3 bg-gray-50 rounded">
                                <Text strong>{mockVendor.business_name}</Text>
                                <br />
                                <Text type="secondary">{mockVendor.description}</Text>
                            </div>
                            <Button
                                type="primary"
                                icon={<BarChartOutlined />}
                                onClick={() => setVendorAnalysisVisible(true)}
                                block
                            >
                                Analyze Vendor
                            </Button>
                            <Text type="secondary">
                                Features: Business analysis, strengths, improvements, trends, comparison
                            </Text>
                        </Space>
                    </Card>
                </Col>

                {/* AI Order Insights */}
                <Col xs={24}>
                    <Card
                        title={
                            <div className="flex items-center gap-2">
                                <BarChartOutlined className="text-indigo-500" />
                                AI Order Insights
                            </div>
                        }
                    >
                        <Paragraph>
                            Test AI analysis of order patterns, predictions, and optimization suggestions.
                        </Paragraph>
                        <Space direction="vertical" className="w-full">
                            <div className="p-3 bg-gray-50 rounded">
                                <Text strong>Mock Order History ({mockOrders.length} orders)</Text>
                                <br />
                                <Text type="secondary">
                                    Total Value: ₹{mockOrders.reduce((sum, order) => sum + order.total, 0)}
                                </Text>
                            </div>
                            <Button
                                type="primary"
                                icon={<BarChartOutlined />}
                                onClick={() => setOrderInsightsVisible(true)}
                                block
                            >
                                Get Order Insights
                            </Button>
                            <Text type="secondary">
                                Features: Pattern analysis, predictions, recommendations, health insights, optimization
                            </Text>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* AI Components - AISupportChat is now handled by Layout component */}

            <AIVendorAnalysis
                vendor={mockVendor}
                visible={vendorAnalysisVisible}
                onClose={() => setVendorAnalysisVisible(false)}
            />

            <AIOrderInsights
                orders={mockOrders}
                visible={orderInsightsVisible}
                onClose={() => setOrderInsightsVisible(false)}
            />

            <Divider />

            <Card title="Integration Summary">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-500">✅</div>
                            <Text strong>Menu Items</Text>
                            <br />
                            <Text type="secondary">AI analysis integrated</Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-500">✅</div>
                            <Text strong>Search</Text>
                            <br />
                            <Text type="secondary">Standard search</Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-500">✅</div>
                            <Text strong>Support</Text>
                            <br />
                            <Text type="secondary">AI customer support</Text>
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-orange-500">✅</div>
                            <Text strong>Vendors</Text>
                            <br />
                            <Text type="secondary">AI vendor analysis</Text>
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default AITestPage;
